import { request } from "@/api/client";
import { PATHS } from "@/api/constants";
import type {
  Airport,
  AirportListQuery,
  PaginatedAirportItems,
  CreateAirportPayload,
  UpdateAirportPayload,
} from "./types";

type ApiEnvelope<T> = { ok: boolean; message?: string; data: T; meta?: any };

function unwrap<T>(res: any): T {
  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    return (res as ApiEnvelope<T>).data;
  }
  return res as T;
}

function qs(params: Record<string, unknown>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s ? `?${s}` : "";
}

const AIRPORTS_BASE = PATHS.airports.base;

export async function listAirports(query: AirportListQuery = {}): Promise<PaginatedAirportItems> {
  const safePage = Math.max(1, Number(query.page ?? 1));
  const rawPageSize = Number(query.limit ?? 20);
  const safePageSize = Math.max(1, Math.min(200, Number.isFinite(rawPageSize) ? rawPageSize : 20));

  const url = `${AIRPORTS_BASE}${qs({
    page: safePage,
    pageSize: safePageSize,
    q: query.q,
    airportCode: query.airportCode,
    cityCode: query.cityCode,
    countryCode: query.countryCode,
    includeDeleted: query.includeDeleted,
  })}`;

  const res = await request<any>({ method: "GET", url });

  if (res && typeof res === "object" && "items" in res && "page" in res && "pageSize" in res && "total" in res) {
    const d = res as PaginatedAirportItems;
    return {
      items: d.items ?? [],
      page: Number(d.page ?? safePage),
      pageSize: Number(d.pageSize ?? safePageSize),
      total: Number(d.total ?? d.items?.length ?? 0),
    };
  }

  if (res && typeof res === "object" && "ok" in res && "data" in res) {
    const env = res as ApiEnvelope<unknown> & { meta?: any };
    const data = env.data;

    if (data && typeof data === "object" && "items" in (data as any)) {
      const d = data as PaginatedAirportItems;
      return {
        items: d.items ?? [],
        page: Number(d.page ?? safePage),
        pageSize: Number(d.pageSize ?? safePageSize),
        total: Number(d.total ?? d.items?.length ?? 0),
      };
    }

    if (Array.isArray(data)) {
      const items = data as Airport[];
      const meta = env.meta ?? {};
      return {
        items,
        page: Number(meta.page ?? safePage),
        pageSize: Number(meta.pageSize ?? meta.limit ?? safePageSize),
        total: Number(meta.total ?? items.length ?? 0),
      };
    }
  }

  return res as PaginatedAirportItems;
}

export async function getAirport(id: number | string): Promise<Airport> {
  const res = await request<Airport | ApiEnvelope<Airport>>({
    method: "GET",
    url: `${AIRPORTS_BASE}/${id}`,
  });
  return unwrap<Airport>(res);
}

export async function createAirport(payload: CreateAirportPayload): Promise<Airport> {
  const res = await request<Airport | ApiEnvelope<Airport>>({
    method: "POST",
    url: AIRPORTS_BASE,
    data: payload,
  });
  return unwrap<Airport>(res);
}

export async function updateAirport(id: number | string, payload: UpdateAirportPayload): Promise<Airport> {
  const res = await request<Airport | ApiEnvelope<Airport>>({
    method: "PUT",
    url: `${AIRPORTS_BASE}/${id}`,
    data: payload,
  });
  return unwrap<Airport>(res);
}

export async function deleteAirport(id: number | string): Promise<{ message: string } | void> {
  const res = await request<{ message: string } | ApiEnvelope<{ message: string }>>({
    method: "DELETE",
    url: `${AIRPORTS_BASE}/${id}`,
  });
  if (!res) return;
  return unwrap<{ message: string }>(res);
}
